import Vue from 'vue'
import VueRouter from 'vue-router'
import { Message } from 'element-ui'
import store from '../store'
import Layout from '../views/Layout.vue'
import { isTokenExpired } from '../utils/token'
import { canAccessRoute, defaultHomePath } from '../utils/permission'

Vue.use(VueRouter)

const routes = [
  { path: '/login', component: () => import('../views/Login.vue') },
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      { path: 'dashboard', component: () => import('../views/Dashboard.vue') },
      { path: 'change-password', component: () => import('../views/ChangePassword.vue') },
      { path: 'projects', component: () => import('../views/Projects.vue') },
      { path: 'users', component: () => import('../views/Users.vue') },
      { path: 'permissions', component: () => import('../views/Permissions.vue') },
      { path: 'teams', redirect: '/entry/docs' },
      { path: 'workers', redirect: '/entry/docs' },
      { path: 'sign/party-roles', component: () => import('../views/sign/PartyRoles.vue') },
      { path: 'training-materials', component: () => import('../views/training-materials/Index.vue') },
      { path: 'training-materials/categories', redirect: '/training-materials' },
      { path: 'training-materials/company', redirect: '/training-materials' },
      { path: 'training-materials/project', redirect: '/training-materials' },
      { path: 'training-materials/team', redirect: '/training-materials' },
      { path: 'sign/templates', component: () => import('../views/sign/Templates.vue') },
      { path: 'sign/party-companies', component: () => import('../views/sign/PartyCompanies.vue') },
      { path: 'sign/tasks', component: () => import('../views/sign/Tasks.vue') },
      { path: 'sign/records', component: () => import('../views/sign/Records.vue') },
      { path: 'exam/questions', component: () => import('../views/exam/Questions.vue') },
      { path: 'exam/records', redirect: '/exam/questions' },
      { path: 'exam/papers', redirect: '/exam/questions' },
      { path: 'exam/tasks', redirect: '/exam/questions' },
      { path: 'files', component: () => import('../views/Files.vue') },
      { path: 'entry/docs', component: () => import('../views/entry/DocsManage.vue') },
      { path: 'entry/personnel-class', component: () => import('../views/entry/DocsManage.vue') },
      { path: 'entry/doc-options', component: () => import('../views/entry/DocOptions.vue') },
      { path: 'entry/training', component: () => import('../views/entry/TrainingHub.vue') },
      { path: 'entry/training/1', component: () => import('../views/entry/TrainingL1.vue') },
      { path: 'entry/training/2', component: () => import('../views/entry/TrainingL2.vue') },
      { path: 'entry/training/3', component: () => import('../views/entry/TrainingL3.vue') },
      { path: 'entry/training/4', component: () => import('../views/entry/TrainingL4.vue') },
      { path: 'entry/contract', component: () => import('../views/entry/ContractSign.vue') },
      { path: 'entry/commitment', component: () => import('../views/entry/CommitmentSign.vue') },
      { path: 'entry/responsibility', component: () => import('../views/entry/ResponsibilitySign.vue') },
      { path: 'entry/sign-overview', component: () => import('../views/entry/SignOverview.vue') },
      { path: 'entry/overview', component: () => import('../views/entry/Overview.vue') },
      { path: 'entry/worker-archive', component: () => import('../views/entry/WorkerArchive.vue') },
      { path: 'exam/scores', component: () => import('../views/exam/ScoreSummary.vue') },
      { path: 'entry/programs', component: () => import('../views/entry/Programs.vue') },
      { path: 'entry/flows', component: () => import('../views/entry/Flows.vue') },
      { path: 'logs', component: () => import('../views/Logs.vue') },
      { path: 'system/wechat', component: () => import('../views/WechatConfig.vue') },
      { path: 'system/exam-config', redirect: '/exam/questions' }
    ]
  },
  { path: '*', redirect: '/dashboard' }
]

const router = new VueRouter({ mode: 'history', routes })

function goLogin(next) {
  next({ path: '/login', replace: true })
}

function goHome(next) {
  const home = defaultHomePath(store.state.user)
  if (!home || home === '/login') {
    store.commit('logout')
    goLogin(next)
    return
  }
  next({ path: home, replace: true })
}

router.beforeEach((to, from, next) => {
  const hasValidToken = store.state.token && !isTokenExpired(store.state.token)

  if (to.path === '/login') {
    if (hasValidToken) {
      const home = defaultHomePath(store.state.user)
      if (home && home !== '/login') {
        next({ path: home, replace: true })
        return
      }
      store.commit('logout')
    }
    next()
    return
  }

  if (!hasValidToken) {
    if (store.state.token) {
      store.commit('logout')
    }
    goLogin(next)
    return
  }

  if (to.path === '/') {
    goHome(next)
    return
  }

  if (!canAccessRoute(store.state.user, to.path)) {
    Message.warning('您没有权限访问该页面')
    if (from.path && from.path !== '/login' && canAccessRoute(store.state.user, from.path)) {
      next(false)
      return
    }
    goHome(next)
    return
  }

  const user = store.state.user
  if (user && user.mustChangePassword && (user.roleCode === 'PROJECT_ADMIN' || user.roleCode === 'PROJECT_SUPERVISOR')
      && to.path !== '/change-password' && to.path !== '/login') {
    next({ path: '/change-password', query: { force: '1' }, replace: true })
    return
  }

  if (to.path === '/system/wechat' || to.path === '/entry/doc-options') {
    if (!user || user.roleCode !== 'SUPER_ADMIN') {
      Message.warning('仅超级管理员可访问')
      goHome(next)
      return
    }
  }

  if (to.path === '/training-materials' || to.path.startsWith('/training-materials/')) {
    if (!user || user.roleCode !== 'SUPER_ADMIN') {
      Message.warning('仅超级管理员可访问')
      goHome(next)
      return
    }
  }

  if (to.matched.length === 0) {
    Message.warning('页面不存在')
    goHome(next)
    return
  }

  next()
})

export default router
